import React, { useEffect, useState } from "react";
import { getMezzi, createMezzo, updateMezzo, deleteMezzo } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Edit2, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nome: "", targa: "", modello: "", kmIniziali: 0, kmAttuali: 0, colore: "#1B5E20", attivo: true };

export default function Mezzi() {
  const [mezzi, setMezzi] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const data = await getMezzi();
    setMezzi(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (m) => { setForm({ ...m }); setEditId(m.id); setOpen(true); };

  const save = async () => {
    const payload = {
      ...form,
      kmIniziali: Number(form.kmIniziali) || 0,
      kmAttuali: Number(form.kmAttuali) || 0,
    };
    try {
      if (editId) {
        await updateMezzo(editId, payload);
        toast.success("Mezzo aggiornato");
      } else {
        await createMezzo(payload);
        toast.success("Mezzo creato");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il mezzo?")) return;
    await deleteMezzo(id);
    toast.success("Mezzo eliminato");
    load();
  };

  return (
    <div className="space-y-4" data-testid="mezzi-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Mezzi</h1>
          <p className="text-slate-500 text-sm mt-1">Flotta furgoni</p>
        </div>
        <Button onClick={openNew} data-testid="new-mezzo-btn" className="bg-[#1B5E20] hover:bg-[#144818] text-white min-h-[44px]">
          <Plus size={18} className="mr-1" /> Nuovo
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 p-4">Caricamento…</div>
      ) : mezzi.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">Nessun mezzo</Card>
      ) : (
        <ul className="space-y-2" data-testid="mezzi-list">
          {mezzi.map((m) => (
            <li key={m.id}>
              <Card className="p-4 bg-white border border-slate-200 shadow-sm card-hover flex items-center gap-3" data-testid={`mezzo-card-${m.id}`}>
                <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: m.colore }}>
                  <Truck size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{m.nome}</div>
                  <div className="text-xs text-slate-500">{m.targa} · {m.modello}</div>
                  <div className="text-xs text-slate-500">Km: {m.kmAttuali}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-mezzo-${m.id}`} className="min-h-[44px] min-w-[44px]"><Edit2 size={18} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(m.id)} data-testid={`delete-mezzo-${m.id}`} className="text-[#C62828] min-h-[44px] min-w-[44px]"><Trash2 size={18} /></Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mezzo-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica mezzo" : "Nuovo mezzo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="mezzo-nome-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Targa</Label>
                <Input value={form.targa} onChange={(e) => setForm({ ...form, targa: e.target.value.toUpperCase() })} data-testid="mezzo-targa-input" />
              </div>
              <div>
                <Label>Modello</Label>
                <Input value={form.modello} onChange={(e) => setForm({ ...form, modello: e.target.value })} data-testid="mezzo-modello-input" />
              </div>
              <div>
                <Label>Km iniziali</Label>
                <Input type="number" value={form.kmIniziali} onChange={(e) => setForm({ ...form, kmIniziali: e.target.value })} />
              </div>
              <div>
                <Label>Km attuali</Label>
                <Input type="number" value={form.kmAttuali} onChange={(e) => setForm({ ...form, kmAttuali: e.target.value })} />
              </div>
              <div>
                <Label>Colore</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.colore} onChange={(e) => setForm({ ...form, colore: e.target.value })} className="h-10 w-14 rounded cursor-pointer" data-testid="mezzo-colore-input" />
                  <Input value={form.colore} onChange={(e) => setForm({ ...form, colore: e.target.value })} />
                </div>
              </div>
              <div className="flex items-end">
                <div className="flex items-center gap-2">
                  <Switch checked={form.attivo} onCheckedChange={(v) => setForm({ ...form, attivo: v })} id="attivo-sw" />
                  <Label htmlFor="attivo-sw">Attivo</Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#1B5E20] hover:bg-[#144818] text-white" data-testid="save-mezzo-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
